import React, { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { debounce } from "lodash";
import { MagnifyingGlassIcon, PlusIcon } from "@heroicons/react/20/solid";
import {
  DocumentPlusIcon,
  ExclamationTriangleIcon,
  SquaresPlusIcon,
  UserPlusIcon,
} from "@heroicons/react/24/outline";
import { AppDispatch, RootState } from "../../../store/store";
import { setParentOrgId, STATUS } from "../../../store/slices/OrganizationSlice";
import { fetchOrganizationDetailsAsync } from "../../../store/thunks/OrganizationThunk";
import {
  fetchOrganizationAncestorsAsync,
  fetchOrganizationsV3Async,
  fetchOrganizationsV3StatsAsync,
  fetchUserOrganizationAsync,
} from "../../../store/thunks/OrganizationsV3Thunk";
import { OrganizationV3, resetOrganizationsV3 } from "../../../store/slices/OrganizationsV3Slice";
import {
  canManageOrganizationCredentials,
  canManageOrganizations,
  canViewOrganizations,
  ORG_STATUS,
  ORG_STATUS_DISPLAY,
  ORGANIZATION_ROLE_LABELS,
  ORGANIZATIONS_PER_PAGE,
} from "../../../constants/organizations";
import { Button } from "../../Catalyst/button";
import { Input, InputGroup } from "../../Catalyst/input";
import { Link } from "../../Catalyst/link";
import { Dropdown, DropdownButton, DropdownItem, DropdownLabel, DropdownMenu } from "../../Catalyst/dropdown";
import { PaginationNext, PaginationPrevious } from "../../Catalyst/pagination";
import StatCard from "../../common/StatCard";
import BackIcon from "../../../assets/BackIcon";
import OrganizationsTable from "./OrganizationsTable";
import DeactivateOrgDialog from "./DeactivateOrgDialog";
import InviteUsersDialog from "../../common/InviteUsersDialog";
import GlobalSearchField from "./GlobalSearchField";
import RenameOrgDialog from "./RenameOrgDialog";
import OrgCredentialsDialog from "./OrgCredentialsDialog";
import UploadBenefitsDialog from "./UploadBenefitsDialog";
import { formatStatValue } from "./utils";

const LoadingState = () => (
  <div className="flex items-center justify-center py-20">
    <svg className="h-8 w-8 animate-spin text-zinc-400" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
      />
    </svg>
  </div>
);

interface OrganizationsDashboardProps {
  // null = top level (State A); a number = drilled into that org (State B).
  selectedOrgId: number | null;
  userRole: string | null;
  // The signed-in user's own organization; names the top-level page title.
  userOrganizationId: number | null;
}

const OrganizationsDashboard = ({
  selectedOrgId,
  userRole,
  userOrganizationId,
}: OrganizationsDashboardProps) => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const isRootLevel = selectedOrgId === null;
  const canManage = canManageOrganizations(userRole);
  const canManageCredentials = canManageOrganizationCredentials(userRole);

  const {
    organizations,
    page,
    totalPages,
    listStatus,
    listError,
    stats,
    ancestors,
    ancestorsOrgId,
    userOrganizationName,
  } = useSelector((state: RootState) => state.OrganizationsV3Store);
  const orgDetails = useSelector((state: RootState) => state.OrganizationListStore?.selectedOrgDetails);

  const [currentPage, setCurrentPage] = useState(1);
  // searchInput drives the controlled field; searchTerm is the debounced value that triggers fetches.
  const [searchInput, setSearchInput] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [deactivateTarget, setDeactivateTarget] = useState<OrganizationV3 | null>(null);
  const [isDeactivateOpen, setIsDeactivateOpen] = useState(false);
  const [renameTarget, setRenameTarget] = useState<OrganizationV3 | null>(null);
  const [isRenameOpen, setIsRenameOpen] = useState(false);
  const [credentialsTarget, setCredentialsTarget] = useState<OrganizationV3 | null>(null);
  const [isCredentialsOpen, setIsCredentialsOpen] = useState(false);
  const [isBenefitsUploadOpen, setIsBenefitsUploadOpen] = useState(false);
  const [isInviteUsersOpen, setIsInviteUsersOpen] = useState(false);

  // Keep the app-wide org context in sync with the level being viewed and load the
  // selected org's details (name, parent) for the header plus its ancestors for
  // the breadcrumb.
  useEffect(() => {
    dispatch(setParentOrgId(selectedOrgId));
    if (selectedOrgId) {
      dispatch(fetchOrganizationDetailsAsync(selectedOrgId));
      dispatch(fetchOrganizationAncestorsAsync(selectedOrgId));
    }
  }, [selectedOrgId, dispatch]);

  // Top-level title: "Welcome back, {Organization} {role}" (see UserListDashboard).
  useEffect(() => {
    if (isRootLevel && userOrganizationId != null && !userOrganizationName) {
      dispatch(fetchUserOrganizationAsync(userOrganizationId));
    }
  }, [isRootLevel, userOrganizationId, userOrganizationName, dispatch]);

  const roleLabel = (userRole && ORGANIZATION_ROLE_LABELS[userRole]) || "";
  const welcomeTitle = userOrganizationName
    ? `Welcome back, ${[userOrganizationName, roleLabel].filter(Boolean).join(" ")}`
    : "Welcome back";

  const fetchList = useCallback(() => {
    dispatch(
      fetchOrganizationsV3Async({
        parentId: selectedOrgId ?? undefined,
        search: searchTerm || undefined,
        page: currentPage,
        perPage: ORGANIZATIONS_PER_PAGE,
      })
    );
  }, [selectedOrgId, searchTerm, currentPage, dispatch]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  useEffect(() => {
    dispatch(fetchOrganizationsV3StatsAsync(selectedOrgId ?? undefined));
  }, [selectedOrgId, dispatch]);

  // Clear v3 results when leaving the page or switching hierarchy level.
  useEffect(() => {
    return () => {
      dispatch(resetOrganizationsV3());
    };
  }, [dispatch]);

  // The list search is a server-side query param; debounce typing.
  const debouncedSetSearch = useMemo(
    () =>
      debounce((value: string) => {
        setSearchTerm(value.trim());
        setCurrentPage(1);
      }, 300),
    []
  );
  useEffect(() => () => debouncedSetSearch.cancel(), [debouncedSetSearch]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchInput(e.target.value);
    debouncedSetSearch(e.target.value);
  };

  const handlePageChange = (nextPage: number) => {
    if (nextPage < 1 || nextPage > totalPages) return;
    setCurrentPage(nextPage);
  };

  // At the top level the header only has the search icon button; ⌘K / Ctrl+K
  // opens the global search page directly (drilled in, GlobalSearchField
  // handles the shortcut by focusing its own input).
  useEffect(() => {
    if (!isRootLevel) return undefined;
    const handleShortcut = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k") {
        event.preventDefault();
        navigate("/search");
      }
    };
    window.addEventListener("keydown", handleShortcut);
    return () => window.removeEventListener("keydown", handleShortcut);
  }, [isRootLevel, navigate]);

  const handleOpenOrganization = useCallback(
    (organization: OrganizationV3) => {
      navigate(`/organizations/${organization.id}`);
    },
    [navigate]
  );

  // ADM-5: the org-scoped user list lives inside the hierarchy pages.
  const handleViewUsers = useCallback(
    (organization: OrganizationV3) => {
      navigate(`/organizations/${organization.id}/users`);
    },
    [navigate]
  );

  const handleDeactivate = useCallback((organization: OrganizationV3) => {
    setDeactivateTarget(organization);
    setIsDeactivateOpen(true);
  }, []);

  const handleRename = useCallback((organization: OrganizationV3) => {
    setRenameTarget(organization);
    setIsRenameOpen(true);
  }, []);

  // Opening the dialog is what triggers the (audited) credentials GET.
  const handleViewCredentials = useCallback((organization: OrganizationV3) => {
    setCredentialsTarget(organization);
    setIsCredentialsOpen(true);
  }, []);

  // Per the API handoff: after a successful deactivation refetch the current page
  // and the stats (the recursive counts above the org change too).
  const handleDeactivated = useCallback(() => {
    fetchList();
    dispatch(fetchOrganizationsV3StatsAsync(selectedOrgId ?? undefined));
  }, [fetchList, selectedOrgId, dispatch]);

  // Details/ancestors in the store may still belong to a previously viewed org;
  // only use them once they match the org on this page.
  const currentOrg = !isRootLevel && orgDetails?.id === selectedOrgId ? orgDetails : null;
  const breadcrumbAncestors = ancestorsOrgId === selectedOrgId ? ancestors : [];

  const handleBack = () => {
    const parentOrgId = currentOrg?.parentOrgId;
    navigate(parentOrgId ? `/organizations/${parentOrgId}` : "/organizations");
  };

  const isLoading = listStatus === STATUS.LOADING || listStatus === STATUS.IDLE;
  const isFailed = listStatus === STATUS.FAILED;
  const isEmpty = listStatus === STATUS.SUCCESS && organizations.length === 0;
  const hasResults = listStatus === STATUS.SUCCESS && organizations.length > 0;

  const renderTableBody = () => {
    if (isLoading) {
      return <LoadingState />;
    }

    if (isFailed) {
      return (
        <div className="flex flex-col items-center justify-center gap-3 py-20">
          <ExclamationTriangleIcon className="h-8 w-8 text-red-400" />
          <p className="max-w-md text-center text-sm font-medium text-red-500">
            {listError || "Something went wrong while loading organizations. Please try again."}
          </p>
        </div>
      );
    }

    if (isEmpty) {
      return (
        <div className="flex flex-col items-center justify-center gap-2 py-20">
          <p className="text-sm font-medium text-texthead opacity-70">No organizations found</p>
          <p className="max-w-md text-center text-sm text-textColor">
            {searchTerm
              ? "There are no organizations matching your search at this level."
              : isRootLevel
                ? "There are no organizations yet."
                : "This organization has no sub-organizations."}
          </p>
        </div>
      );
    }

    return (
      <div className="px-6">
        <OrganizationsTable
          organizations={organizations}
          canManage={canManage}
          canManageCredentials={canManageCredentials}
          onOpenOrganization={handleOpenOrganization}
          onViewUsers={handleViewUsers}
          onRename={handleRename}
          onViewCredentials={handleViewCredentials}
          onDeactivate={handleDeactivate}
        />
      </div>
    );
  };

  return (
    <div className="p-8">
      {/* Page header */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex min-w-0 items-center gap-3">
          {isRootLevel ? (
            <h1 className="desktext10 truncate">{welcomeTitle}</h1>
          ) : (
            <>
              <Button
                outline
                onClick={handleBack}
                title="Back to parent organization"
                aria-label="Back to parent organization"
              >
                <BackIcon />
              </Button>
              <h1 className="desktext10 truncate">{currentOrg?.name ?? ""}</h1>
              {/* Deactivated orgs stay browsable; flag the state so the drilled-in
                  view is not mistaken for an active org. */}
              {currentOrg?.is_active === false && (
                <span
                  className={`shrink-0 rounded-full px-3 py-1 text-sm font-medium ${
                    ORG_STATUS_DISPLAY[ORG_STATUS.DEACTIVATED].pillClass
                  }`}
                >
                  {ORG_STATUS_DISPLAY[ORG_STATUS.DEACTIVATED].label}
                </span>
              )}
            </>
          )}
        </div>
        <div className="flex shrink-0 items-center gap-4">
          {isRootLevel ? (
            <Button
              outline
              onClick={() => navigate("/search")}
              title="Search (⌘K)"
              aria-label="Search"
            >
              <MagnifyingGlassIcon />
            </Button>
          ) : (
            <GlobalSearchField className="hidden w-[360px] md:block xl:w-[490px]" />
          )}
          {canManage &&
            (isRootLevel ? (
              <Button
                color="dark"
                onClick={() => navigate("/create-org", { state: { backTo: "/organizations" } })}
              >
                <PlusIcon />
                Create
              </Button>
            ) : (
              <Dropdown>
                <DropdownButton color="dark">
                  <PlusIcon />
                  Create
                </DropdownButton>
                <DropdownMenu anchor="bottom end">
                  {/* No "New Organization" here: inside an org it duplicates
                      New Sub-Organization. backTo returns Cancel/Okay to this
                      screen instead of org-view. */}
                  <DropdownItem
                    onClick={() =>
                      navigate(`/create-org/${selectedOrgId}`, {
                        state: { backTo: `/organizations/${selectedOrgId}` },
                      })
                    }
                  >
                    <SquaresPlusIcon />
                    <DropdownLabel>New Sub-Organization</DropdownLabel>
                  </DropdownItem>
                  {/* Opens the same invite flow (CSV or manual) as the user list
                      dashboard; the standalone /create-user screen is unused. */}
                  <DropdownItem onClick={() => setIsInviteUsersOpen(true)}>
                    <UserPlusIcon />
                    <DropdownLabel>Invite Users</DropdownLabel>
                  </DropdownItem>
                  <DropdownItem onClick={() => setIsBenefitsUploadOpen(true)}>
                    <DocumentPlusIcon />
                    <DropdownLabel>Upload Benefit Docs</DropdownLabel>
                  </DropdownItem>
                </DropdownMenu>
              </Dropdown>
            ))}
        </div>
      </div>

      {/* Breadcrumb (only when drilled into an org) */}
      {!isRootLevel && (
        <div className="mt-4 flex flex-wrap items-center gap-2 text-sm font-medium text-texthead">
          <Link href="/organizations" className="hover:underline">
            Organization
          </Link>
          {breadcrumbAncestors.map((ancestor) => (
            <Fragment key={ancestor.id}>
              <span className="opacity-50">/</span>
              <Link href={`/organizations/${ancestor.id}`} className="hover:underline">
                {ancestor.name}
              </Link>
            </Fragment>
          ))}
          <span className="opacity-50">/</span>
          <span aria-current="page">{currentOrg?.name ?? ""}</span>
        </div>
      )}

      {/* Summary cards. The stats fields do not change between states — only the
          card labels do (see the ADM-6 API handoff). */}
      <div className="mt-6 grid grid-cols-1 gap-5 lg:grid-cols-2">
        <StatCard
          title={isRootLevel ? "Active Organizations" : "Active Sub Organizations"}
          description={
            isRootLevel
              ? "Total active organizations underneath this organization and all organizations beneath it"
              : "Total active organizations underneath this organization"
          }
          value={formatStatValue(stats?.totalActiveSubOrgs)}
          valueLabel={isRootLevel ? "active orgs" : "active sub-orgs"}
        />
        <StatCard
          title="Total Active Users Under All Organizations"
          description={
            isRootLevel
              ? "Active users nested under this organization and all organizations beneath it"
              : "Active users in this organization and all sub-organizations"
          }
          value={formatStatValue(stats?.totalActiveUsers)}
          valueLabel="active users"
        />
      </div>

      {/* Organizations table */}
      <div className="mt-6 w-full rounded-lg border-2 border-shadowborder pt-5">
        <div className="flex flex-col gap-3 px-6 pb-4">
          <div>
            <div className="desktext11 text-texthead">
              {isRootLevel ? "Your Organizations" : "Your Sub Organizations"}
            </div>
            {!isRootLevel && (
              <div className="mt-1 font-inter text-sm font-normal leading-5 text-textColor">
                Find organizations you&rsquo;ve set up under this organization
              </div>
            )}
          </div>
          <div className="w-full max-w-[490px]">
            <InputGroup>
              <MagnifyingGlassIcon />
              <Input
                name="search"
                value={searchInput}
                onChange={handleSearchChange}
                placeholder="Search organizations"
                aria-label="Search organizations"
              />
            </InputGroup>
          </div>
        </div>

        {renderTableBody()}

        {hasResults && (
          <div className="flex items-center justify-between border-t border-shadowborder px-6 py-3">
            <span className="text-sm font-medium text-textColor3">
              Page {page} of {totalPages}
            </span>
            <div className="flex items-center gap-3">
              <PaginationPrevious
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
              />
              <PaginationNext
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
              />
            </div>
          </div>
        )}
      </div>

      <DeactivateOrgDialog
        organization={deactivateTarget}
        open={isDeactivateOpen}
        onClose={() => setIsDeactivateOpen(false)}
        onDeactivated={handleDeactivated}
      />

      <RenameOrgDialog
        organization={renameTarget}
        open={isRenameOpen}
        onClose={() => setIsRenameOpen(false)}
      />

      {/* Super Admin only; the menu entry is gated on canManageCredentials. */}
      <OrgCredentialsDialog
        organization={credentialsTarget}
        open={isCredentialsOpen}
        onClose={() => setIsCredentialsOpen(false)}
      />

      {!isRootLevel && selectedOrgId != null && (
        <InviteUsersDialog
          orgName={currentOrg?.name ?? "this organization"}
          open={isInviteUsersOpen}
          onClose={() => setIsInviteUsersOpen(false)}
        />
      )}

      {/* Benefits packages are per-org, so this is only reachable once drilled into
          an organization. Uploading replaces the org's existing package server-side. */}
      {!isRootLevel && selectedOrgId != null && (
        <UploadBenefitsDialog
          orgId={selectedOrgId}
          orgName={currentOrg?.name ?? "this organization"}
          open={isBenefitsUploadOpen}
          onClose={() => setIsBenefitsUploadOpen(false)}
        />
      )}
    </div>
  );
};

const Organizations = () => {
  const { orgId } = useParams<{ orgId: string }>();
  const userAccount = useSelector((state: RootState) => state.OrganizationListStore?.userAccount);

  // Deliberately not useOrgId(): landing on /organizations must always show the
  // top level, regardless of any org context persisted in Redux.
  const parsedOrgId = orgId ? parseInt(orgId, 10) : NaN;
  const selectedOrgId = Number.isNaN(parsedOrgId) ? null : parsedOrgId;

  // Access control: userAccount is fetched in AppLayout; treat its absence as "still loading".
  if (!userAccount) {
    return (
      <div className="p-8">
        <LoadingState />
      </div>
    );
  }

  if (!canViewOrganizations(userAccount.user_role)) {
    return (
      <div className="p-8">
        <div className="mt-10 flex flex-col items-center justify-center gap-3 text-center">
          <ExclamationTriangleIcon className="h-8 w-8 text-amber-500" />
          <div className="desktext14 text-texthead">Access restricted</div>
          <p className="max-w-md text-sm text-textColor">
            You do not have permission to view organizations. Please contact an administrator if
            you believe this is a mistake.
          </p>
        </div>
      </div>
    );
  }

  // Remount the dashboard when the hierarchy level changes so page and search
  // state reset cleanly instead of leaking between levels.
  return (
    <OrganizationsDashboard
      key={selectedOrgId ?? "root"}
      selectedOrgId={selectedOrgId}
      userRole={userAccount.user_role}
      userOrganizationId={userAccount.organization_id ? Number(userAccount.organization_id) : null}
    />
  );
};

export default Organizations;
